import { useTranslation } from 'react-i18next';
import { getIn, useFormikContext } from 'formik';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { RuleField } from '../../types';
import { emptyColorCaseRow, ColorCaseFormValue } from '../../utils';
import { ColorCaseRow } from './ColorCaseRow';

export interface ColorCaseListEditorProps {
  readonly name: string;
  // Gate conditions pick their OWN field per row; a scoring rule's cases share ONE fixed field.
  readonly showFieldPicker: boolean;
  readonly fields?: RuleField[];
  readonly fixedField?: RuleField;
  readonly showPoints: boolean;
  readonly disabled: boolean;
  readonly emptyMessage: string;
}

export const ColorCaseListEditor = ({
  name,
  showFieldPicker,
  fields,
  fixedField,
  showPoints,
  disabled,
  emptyMessage,
}: ColorCaseListEditorProps) => {
  const { t } = useTranslation('common');
  const { values, setFieldValue } = useFormikContext<unknown>();

  const rows = (getIn(values, name) as ColorCaseFormValue[] | undefined) ?? [];

  const replaceRows = (next: ColorCaseFormValue[]) => setFieldValue(name, next);

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;

    if (target < 0 || target >= rows.length) {
      return;
    }

    const next = [...rows];

    [next[index], next[target]] = [next[target], next[index]];
    replaceRows(next);
  };

  return (
    <Box>
      {!rows.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {emptyMessage}
        </Typography>
      )}

      {rows.map((row, index) => (
        <ColorCaseRow
          key={row.uid}
          name={`${name}.${index}`}
          index={index}
          showFieldPicker={showFieldPicker}
          fields={fields}
          fixedField={fixedField}
          showPoints={showPoints}
          disabled={disabled}
          canRemove={rows.length > 1}
          canMoveUp={index > 0}
          canMoveDown={index < rows.length - 1}
          onRemove={() => replaceRows(rows.filter((_row, rowIndex) => rowIndex !== index))}
          onMove={direction => handleMove(index, direction)}
        />
      ))}

      {!disabled && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => replaceRows([...rows, emptyColorCaseRow()])}>
          {t('rules.colorCoding.row.addButton')}
        </Button>
      )}
    </Box>
  );
};
